import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Country, COUNTRY_CODES } from '../../../models/country-codes.model';
import { EMPTY, Observable } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { DokumenteComponent, fileSizeValidator } from '../dokumente/dokumente.component';
import { EhrenamtComponent, TAETIGKEIT_MAX_LENGTH } from '../ehrenamt/ehrenamt.component';
import { BildungComponent, schuleRequiredValidator } from '../bildung/bildung.component';
import { BewerberAdapter } from '../../../adapter/bewerber.adapter';
import { Router } from '@angular/router';
import { StateService } from '../../../services/state.service';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/angular';
import { Bewerber, FileDocument } from '../../../models/bewerber.model';
import { AbschlussKategorie, Ausbildung, Berufserfahrung, BewerbungUnterlagen, Bild, Ehrenamt } from '../../../models/unterlagen.model';
import { DatePipe } from '@angular/common';
import { FotoComponent } from '../foto/foto.component';
import { WerdegangComponent } from '../werdegang/werdegang.component';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { Card } from 'primeng/card';
import { GESCHLECHT_CATEGORIES, GeschlechtCategory } from '../../../models/geschlecht.model';
import { RadioButton } from 'primeng/radiobutton';
import { InputText } from 'primeng/inputtext';
import { TrimInputValueDirective } from '../../../services/form/trim-input-value.directive';
import {
  emailAdresseValidator,
  festnetzTelefonNummerValidator,
  geburtsdatumValidator,
  hausNummerValidator,
  ortNameValidator,
  personNameValidator,
  postleitzahlValidator,
  strasseNameValidator,
  telefonNummerValidator
} from '../../../services/form/bewerber-validators';
import { FieldErrorComponent } from '../../common/field-error/field-error.component';
import { Select } from 'primeng/select';
import { TauresMessageModule } from '@taures/taures-components/taures-message';
import { Button } from 'primeng/button';
import { ToastMessageEmitterService, ToastSeverity } from '@taures/taures-components/taures-toast';
import {
  AusbildungForm,
  BerufserfahrungFormGroup,
  BewerbungEinreichenForm,
  BewerbungsunterlagenFormGroup,
  EhrenamtFormGroup,
  InteressentFormGroup
} from './bewerbung-einreichen-form-groups';
import { TauresConfirmationDialogService } from '@taures/taures-components/taures-confirm-dialog';
import { MOTIVATION_MAX_LENGTH, MOTIVATION_MIN_LENGTH, MotivationComponent } from '../motivation/motivation.component';
import { SONSTIGES_MAX_LENGTH, SonstigesComponent } from '../interessen/sonstiges.component';
import { PrimeTemplate } from 'primeng/api';
import { VerificationResponseDto } from '../../../adapter/dto/verification-response.dto';
import { ContactData } from '../../../adapter/dto/contact-data.dto';

const urlValidator: ValidatorFn = (control: AbstractControl): { invalidUrl: true } | null => {
  if (control.value === null || control.value === '') {
    return null;
  }
  try {
    new URL(control.value as string);
  } catch {
    return { invalidUrl: true };
  }
  return null;
};

@Component({
  selector: 'app-bewerbung-einreichen',
  templateUrl: './bewerbung-einreichen.component.html',
  styleUrls: ['./bewerbung-einreichen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    EhrenamtComponent,
    BildungComponent,
    ReactiveFormsModule,
    FotoComponent,
    WerdegangComponent,
    DatePicker,
    FloatLabel,
    Card,
    RadioButton,
    InputText,
    TrimInputValueDirective,
    Select,
    TauresMessageModule,
    Button,
    DokumenteComponent,
    MotivationComponent,
    SonstigesComponent,
    PrimeTemplate,
    FieldErrorComponent
  ],
  standalone: true
})
export class BewerbungEinreichenComponent {
  protected readonly stateService: StateService = inject(StateService);
  private readonly router: Router = inject(Router);
  private readonly bewerberService: BewerberAdapter = inject(BewerberAdapter);
  private readonly toastMessageEmitterService: ToastMessageEmitterService = inject(ToastMessageEmitterService);
  private readonly confirmationDialogService: TauresConfirmationDialogService = inject(TauresConfirmationDialogService);

  protected readonly geschlechtCategories: GeschlechtCategory[] = GESCHLECHT_CATEGORIES;
  protected readonly sortedCountries: Country[] = COUNTRY_CODES;

  protected readonly anmeldeschluss: string | null = this.stateService.getAnmeldeschluss();
  protected readonly vpManager: ContactData | null = this.stateService.getVpManager();
  private readonly birthdate: Date | null = this.stateService.getBirthdate();
  private readonly token: string | null = this.stateService.getToken();

  protected readonly form: FormGroup<BewerbungEinreichenForm>;

  public constructor() {
    if (this.token === null || this.birthdate === null) {
      this.showDefaultErrorToastMessage();
    }

    if (this.anmeldeschluss === null) {
      this.router.navigate(['/vp', this.token, 'anmeldung-geloescht'], { replaceUrl: true });
    }

    // Use effects to sync signals with component properties and form
    effect((): void => {
      const applicantData: Bewerber | null = this.stateService.getBewerber();
      if (applicantData === null) {
        return;
      }

      const interessentForm: FormGroup<InteressentFormGroup> = this.form.controls.interessent;

      interessentForm.patchValue(applicantData);
      interessentForm.controls.geburtsdatum.setValue(this.birthdate);
    });

    this.form = new FormGroup({
      interessent: new FormGroup({
        geschlecht: new FormControl<string | null>(null, { validators: [Validators.required] }),
        vorname: new FormControl<string | null>(null, { validators: [Validators.required, personNameValidator] }),
        nachname: new FormControl<string | null>(null, { validators: [Validators.required, personNameValidator, Validators.maxLength(50)] }),
        geburtsname: new FormControl<string | null>(null, { validators: [personNameValidator, Validators.maxLength(50)] }),
        rufname: new FormControl<string | null>(null, { validators: [personNameValidator, Validators.maxLength(50)] }),
        geburtsdatum: new FormControl<Date | null>(null, { validators: [Validators.required, geburtsdatumValidator] }),
        nationalitaet: new FormControl<string | null>(null, { validators: [Validators.required] }),
        strasse: new FormControl<string | null>(null, { validators: [Validators.required, strasseNameValidator] }),
        hausnummer: new FormControl<string | null>(null, { validators: [Validators.required, hausNummerValidator] }),
        plz: new FormControl<string | null>(null, { validators: [Validators.required, postleitzahlValidator] }),
        stadt: new FormControl<string | null>(null, { validators: [Validators.required, ortNameValidator] }),
        telefonMobil: new FormControl<string | null>(null, { validators: [Validators.required, telefonNummerValidator] }),
        telefonPrivat: new FormControl<string | null>(null, { validators: [festnetzTelefonNummerValidator, Validators.maxLength(100)] }),
        email: new FormControl<string | null>(null, { validators: [Validators.required, emailAdresseValidator, Validators.maxLength(100)] })
      }),
      bewerbungsunterlagen: new FormGroup({
        bild: new FormControl<Bild | null>(null),
        berufserfahrung: new FormArray<FormGroup<BerufserfahrungFormGroup>>([]),
        ausbildung: new FormArray<AusbildungForm>([], { validators: [schuleRequiredValidator] }),
        ehrenamt: new FormArray<FormGroup<EhrenamtFormGroup>>([]),
        sonstiges: new FormControl<string | null>(null, { validators: Validators.maxLength(SONSTIGES_MAX_LENGTH) }),
        motivation: new FormControl<string | null>(null, {
          validators: [Validators.required, Validators.minLength(MOTIVATION_MIN_LENGTH), Validators.maxLength(MOTIVATION_MAX_LENGTH)]
        })
      }),
      dokumente: new FormControl<FileDocument[]>([], {
        validators: [Validators.required, Validators.maxLength(10), fileSizeValidator(15 * 1024 * 1024)]
      })
    }) as FormGroup<BewerbungEinreichenForm>;
  }

  protected addBerufserfahrung(): void {
    this.form.controls.bewerbungsunterlagen.controls.berufserfahrung.push(
      new FormGroup({
        positionsbezeichnung: new FormControl<string | null>(null, { validators: [Validators.required] }),
        beschaeftigungsart: new FormControl<string | null>(null, { validators: [Validators.required] }),
        unternehmen: new FormControl<string | null>(null),
        url: new FormControl<string | null>(null, { validators: [urlValidator] }),
        branche: new FormControl<string | null>(null, { validators: [Validators.required] }),
        von: new FormControl<Date | null>(null, { validators: [Validators.required] }),
        bis: new FormControl<Date | null>(null)
      })
    );
  }

  protected addEhrenamt(): void {
    this.form.controls.bewerbungsunterlagen.controls.ehrenamt.push(
      new FormGroup({
        institution: new FormControl<string | null>(null, { validators: [Validators.required] }),
        taetigkeit: new FormControl<string | null>(null, { validators: [Validators.maxLength(TAETIGKEIT_MAX_LENGTH)] }),
        von: new FormControl<Date | null>(null, { validators: [Validators.required] }),
        bis: new FormControl<Date | null>(null)
      })
    );
  }

  protected removeBerufserfahrung(i: number): void {
    this.form.controls.bewerbungsunterlagen.controls.berufserfahrung.removeAt(i);
  }

  protected removeEhrenamt(i: number): void {
    this.form.controls.bewerbungsunterlagen.controls.ehrenamt.removeAt(i);
  }

  public geschlechtNotSelected(): boolean {
    return this.form.controls.interessent.controls.geschlecht.touched && this.form.controls.interessent.controls.geschlecht.invalid;
  }

  private markFormGroupDirty(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach((key: string): void => {
      const control: AbstractControl | null = formGroup.get(key);

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupDirty(control);
      } else if (control instanceof FormControl) {
        control.markAsDirty();
      }
    });
  }

  protected onSubmit(): void {
    if (this.anmeldeschluss !== null && DateTime.now() > DateTime.fromISO(this.anmeldeschluss) && this.vpManager !== null) {
      this.showAnmeldeschlussAbgelaufenToastMessage(this.anmeldeschluss, this.vpManager);
      return;
    }

    this.markFormGroupDirty(this.form);
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.toastMessageEmitterService.publishToastMessage({
        severity: ToastSeverity.USER_OPERATION_ERROR,
        message: 'Einige Einträge sind nicht gültig oder fehlen. Bitte überprüfe das Formular.'
      });
      this.focusFirstElementWithError();
      return;
    }

    this.confirmationDialogService.confirmWithTauresConfirmDialog({
      header: 'Bewerbung absenden?',
      message: 'Möchtest du wirklich deine Bewerbung jetzt abschicken? Änderungen sind nach dem Absenden nicht mehr möglich.',
      key: 'warn-confirm-dialog',
      acceptLabel: 'Bewerbung absenden',
      accept: (): void => {
        this.bewerbungAbschicken();
      }
    });
  }

  private focusFirstElementWithError(): void {
    const firstInvalidElement: HTMLElement | null = document.querySelector<HTMLElement>(
      '.p-inputtext.ng-invalid, .p-textarea.ng-invalid, .p-message-error, p-datePicker.ng-invalid, p-datepicker.ng-invalid'
    );

    if (firstInvalidElement !== null) {
      firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalidElement.focus();
    }
  }

  private bewerbungAbschicken(): void {
    const interessent: Bewerber = this.getInteressentFromForm();
    const bewerbungsunterlagen: BewerbungUnterlagen = this.getBewerbungsunterlagenFromForm();
    const dokumente: FileDocument[] = this.form.controls.dokumente.value;

    this.stateService.setIsLoading(true);
    this.stateService.setHasError(false);

    if (this.token !== null && this.birthdate !== null) {
      this.bewerberService
        .sendCV(this.token, this.birthdate, interessent, bewerbungsunterlagen, dokumente)
        .pipe(
          take(1),
          catchError((err: Error): Observable<null> => {
            Sentry.captureEvent(err);
            this.handleBewerbungError();
            return EMPTY;
          })
        )
        .subscribe((): void => {
          this.stateService.setIsLoading(false);
          this.stateService.setHasError(false);
          this.stateService.setBirthdate(interessent.geburtsdatum); // set birthdate again since it could've been changed
          this.stateService.bewerbungsunterlagenEingereicht();
          this.router.navigate(['/vp', this.token, 'bewerbung-eingereicht'], { replaceUrl: true });
        });
    }
  }

  private handleBewerbungError(): void {
    this.stateService.setIsLoading(false);
    this.stateService.setHasError(true);

    this.bewerberService
      .verify(this.token!, this.birthdate!)
      .pipe(take(1))
      .subscribe((verificationResponse: VerificationResponseDto): void => {
        const currentAnmeldeschluss: string | null = verificationResponse.anmeldeschluss ?? null;
        const isSameAnmeldeschluss: boolean =
          this.anmeldeschluss !== null && DateTime.fromISO(currentAnmeldeschluss!).hasSame(DateTime.fromISO(this.anmeldeschluss), 'day');

        if (this.vpManager !== null) {
          if (currentAnmeldeschluss === null) {
            this.toastMessageEmitterService.publishToastMessage({
              messageTitle: 'Deine Anmeldung zum Assessment-Center wurde gelöscht',
              severity: ToastSeverity.TECHNICAL_ERROR,
              sticky: true,
              message: `Bitte wende dich an ${this.vpManager.person.vorname} ${this.vpManager.person.nachname}, um zu prüfen, ob ein anderes Datum in Frage kommt.`
            });

            this.toastMessageEmitterService.publishToastMessage({
              severity: ToastSeverity.TECHNICAL_ERROR,
              sticky: true,
              message:
                `Sobald du für einen anderen Termin vorgemerkt bist, erhältst du von uns eine E-Mail mit dem neuen Termin. ` +
                `Du erreichst das Bewerbungsportal zum Einreichen deiner Bewerbung weiterhin über deinen persönlichen Link.`
            });
            return;
          } else if (this.anmeldeschluss !== null && !isSameAnmeldeschluss) {
            this.stateService.setVerificationResponse(verificationResponse);
            this.showAnmeldeschlussAbgelaufenToastMessage(this.anmeldeschluss, this.vpManager);
            return;
          }
        }

        // fallback error message
        this.showDefaultErrorToastMessage();
      });
  }

  private showDefaultErrorToastMessage(): void {
    this.toastMessageEmitterService.publishToastMessage({
      messageTitle: 'Ups! Das hat leider nicht geklappt',
      severity: ToastSeverity.TECHNICAL_ERROR,
      sticky: true,
      message: `Bitte versuche es zu einem späteren Zeitpunkt noch einmal.`
    });
  }

  private showAnmeldeschlussAbgelaufenToastMessage(previousAnmeldeschluss: string, manager: ContactData): void {
    this.toastMessageEmitterService.publishToastMessage({
      messageTitle: `Der Anmeldeschluss für das AC1 ist am ${DateTime.fromISO(previousAnmeldeschluss).toFormat('dd.MM.yyyy')} um ${DateTime.fromISO(previousAnmeldeschluss).toFormat('HH:mm')} Uhr abgelaufen.`,
      severity: ToastSeverity.TECHNICAL_ERROR,
      sticky: true,
      message:
        `Bitte wende dich an ${manager.person.vorname} ${manager.person.nachname}, um zu prüfen, ob ein anderes Datum in Frage kommt. Sobald du für einen ` +
        `anderen Termin vorgemerkt bist, erhältst du von uns eine E-mail mit dem neuen Termin. Du erreichst das Bewerbungsportal zum Einreichen deiner ` +
        `Bewerbung weiterhin über deinen persönlichen Link.`
    });
  }

  private getInteressentFromForm(): Bewerber {
    return {
      vorname: this.form.controls.interessent.value.vorname!,
      nachname: this.form.controls.interessent.value.nachname!,
      rufname: this.form.controls.interessent.value.rufname ?? null,
      geburtsname: this.form.controls.interessent.value.geburtsname ?? null,
      geburtsdatum: this.form.controls.interessent.value.geburtsdatum!,
      geschlecht: this.form.controls.interessent.value.geschlecht!,
      email: this.form.controls.interessent.value.email!,
      hausnummer: this.form.controls.interessent.value.hausnummer!,
      nationalitaet: this.form.controls.interessent.value.nationalitaet!,
      plz: this.form.controls.interessent.value.plz!,
      stadt: this.form.controls.interessent.value.stadt!,
      strasse: this.form.controls.interessent.value.strasse!,
      telefonMobil: this.form.controls.interessent.value.telefonMobil!,
      telefonPrivat: this.form.controls.interessent.value.telefonPrivat ?? null
    };
  }

  private getBewerbungsunterlagenFromForm(): BewerbungUnterlagen {
    const bewerbungsunterlagen: FormGroup<BewerbungsunterlagenFormGroup> = this.form.controls.bewerbungsunterlagen;
    if (bewerbungsunterlagen.value.bild !== null && bewerbungsunterlagen.value.bild?.src === undefined) {
      bewerbungsunterlagen.value.bild = null;
    }

    return {
      bild: bewerbungsunterlagen.value.bild ?? null,
      berufserfahrung: this.form.controls.bewerbungsunterlagen.controls.berufserfahrung.controls.map(
        (group: FormGroup<BerufserfahrungFormGroup>): Berufserfahrung => ({
          positionsbezeichnung: group.value.positionsbezeichnung!,
          beschaeftigungsart: group.value.beschaeftigungsart!,
          unternehmen: group.value.unternehmen!,
          url: group.value.url!,
          branche: group.value.branche!,
          von: group.value.von!,
          bis: group.value.bis!
        })
      ),
      ausbildung: this.form.controls.bewerbungsunterlagen.controls.ausbildung.controls.map((control: AusbildungForm): Ausbildung => {
        switch (control.value.typ) {
          case AbschlussKategorie.schule:
            return {
              typ: AbschlussKategorie.schule,
              name: control.value.name!,
              abschluss: control.value.abschluss ?? null,
              abschlussTyp: control.value.abschlussTyp ?? null,
              ende: control.value.ende ?? null
            };
          case AbschlussKategorie.studium:
            return {
              typ: AbschlussKategorie.studium,
              hochschule: control.value.hochschule!,
              fachrichtung: control.value.fachrichtung ?? null,
              abschluss: control.value.abschluss ?? null,
              andererAbschluss: control.value.andererAbschluss ?? null,
              abschlussTyp: control.value.abschlussTyp ?? null,
              von: control.value.von!,
              bis: control.value.bis ?? null
            };
          case AbschlussKategorie.lehre:
            return {
              typ: AbschlussKategorie.lehre,
              unternehmen: control.value.unternehmen!,
              branche: control.value.branche ?? null,
              abschluss: control.value.abschluss ?? null,
              abschlussTyp: control.value.abschlussTyp!,
              von: control.value.von!,
              bis: control.value.bis ?? null
            };
          case undefined:
            throw new Error(`Unexpected AusbildungForm.typ: undefined`);
          default:
            throw new Error(`Unexpected AusbildungForm.typ`);
        }
      }),
      ehrenamt: this.form.controls.bewerbungsunterlagen.controls.ehrenamt.controls.map((group: FormGroup<EhrenamtFormGroup>): Ehrenamt => ({
        institution: group.value.institution!,
        taetigkeit: group.value.taetigkeit!,
        von: group.value.von!,
        bis: group.value.bis!
      })),
      sonstiges: bewerbungsunterlagen.controls.sonstiges.value ?? null,
      motivation: bewerbungsunterlagen.controls.motivation.value!
    };
  }
}
